import { useState, useRef, useEffect } from 'react';
import { Menu, Bell, Search, BellOff, User, X, Check, Calendar, AlertCircle, Users, UserMinus, Mail, MapPin, BookOpen } from 'lucide-react';
import { useLocationNavigate, useLocationSubPath } from '@/hooks/useLocationNavigate';
import { Button } from '@/components/ui/Button';
import { LocationSelector } from '@/components/layout/LocationSelector';
import { PlaybookModal } from '@/components/layout/PlaybookModal';
import { useContactSearch } from '@/hooks/useContactSearch';
import { useContactList } from '@/hooks/useContactList';
import { useNotifications, useUnreadCount, useNotificationMutations } from '@/hooks/useNotifications';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationContext } from '@/contexts/LocationContext';
import { trpc } from '@/trpc/client';
import type { Notification, NotificationEventType } from 'shared';

export interface HeaderProps {
  onMenuClick: () => void;
  onWalkInClick?: () => void;
  pageTitle?: string;
  showSearch?: boolean;
  showActions?: boolean;
}

/**
 * Get icon and color for notification type
 */
function getNotificationStyle(type: NotificationEventType): { icon: React.ReactNode; color: string } {
  switch (type) {
    case 'new_appointment':
      return { icon: <Calendar className="w-4 h-4" />, color: 'text-green-400' };
    case 'appointment_cancelled':
      return { icon: <X className="w-4 h-4" />, color: 'text-red-400' };
    case 'appointment_rescheduled':
      return { icon: <Calendar className="w-4 h-4" />, color: 'text-orange-400' };
    case 'ai_booking':
      return { icon: <Calendar className="w-4 h-4" />, color: 'text-blue-400' };
    case 'overdue_pickup':
      return { icon: <AlertCircle className="w-4 h-4" />, color: 'text-yellow-400' };
    case 'rcs_takeover':
      return { icon: <AlertCircle className="w-4 h-4" />, color: 'text-purple-400' };
    case 'new_contact':
      return { icon: <Users className="w-4 h-4" />, color: 'text-green-400' };
    case 'contact_deleted':
      return { icon: <UserMinus className="w-4 h-4" />, color: 'text-red-400' };
    case 'new_message':
      return { icon: <Mail className="w-4 h-4" />, color: 'text-blue-400' };
    default:
      return { icon: <Bell className="w-4 h-4" />, color: 'text-slate-400' };
  }
}

/**
 * Format notification timestamp
 * Accepts both Date objects and ISO date strings (from API)
 */
function formatTime(date: Date | string): string {
  const now = new Date();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const diff = now.getTime() - dateObj.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Gerade eben';
  if (minutes < 60) return `vor ${minutes} Min.`;
  if (hours < 24) return `vor ${hours} Std.`;
  if (days === 1) return 'Gestern';
  return `vor ${days} Tagen`;
}

/**
 * Notification from API (dates are strings)
 */
type ApiNotification = Omit<Notification, 'createdAt' | 'readAt'> & {
  createdAt: string;
  readAt: string | null;
};

export function Header({ onMenuClick, onWalkInClick, pageTitle = 'Dashboard', showSearch = true, showActions = true }: HeaderProps) {
  const locationNavigate = useLocationNavigate();
  const locationSubPath = useLocationSubPath();
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Location context for location switcher
  const { selectedLocationId, setSelectedLocationId, isSuperAdmin, initializeDefault } = useLocationContext();

  // Fetch all locations and user's assigned locations
  const { data: locations } = trpc.location.list.useQuery({});
  const { data: userLocations } = trpc.user.getLocations.useQuery(
    { userId: user?.id ?? '' },
    { enabled: !!user?.id && !isSuperAdmin }
  );

  // For non-super_admin: filter locations to only those assigned to the user
  const assignedLocationIds = userLocations?.map((ul: { locationId: string }) => ul.locationId) ?? [];
  const userAssignedLocations = locations?.filter((l: { id: string; isActive: boolean }) =>
    l.isActive && assignedLocationIds.includes(l.id)
  ) ?? [];
  const hasMultipleLocations = !isSuperAdmin && userAssignedLocations.length > 1;

  const effectiveLocationId = selectedLocationId || user?.primaryLocationId;
  const locationName = locations?.find((l: { id: string }) => l.id === effectiveLocationId)?.name;

  // When location is switched via dropdown, navigate to the new slug URL
  // Navigate FIRST so LocationSync picks up the new slug, then state follows
  const handleLocationSwitch = (locationId: string | undefined) => {
    if (locationId && locations) {
      const loc = locations.find(l => l.id === locationId);
      if (loc?.slug) {
        // Drop detail IDs from path (contact from location A won't exist in location B)
        const basePath = locationSubPath.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
        // Set state first so the UI updates immediately, then navigate
        setSelectedLocationId(locationId);
        // Use window.location for a full navigation to avoid LocationSync race condition
        window.location.href = `/location/${loc.slug}${basePath}`;
        return;
      }
    }
    if (!locationId) {
      setSelectedLocationId(undefined);
      window.location.href = '/superadmin';
      return;
    }
    setSelectedLocationId(locationId);
  };

  // Default to first active location for super_admin
  useEffect(() => {
    if (isSuperAdmin && locations) {
      const activeLocations = locations.filter((l: { isActive: boolean }) => l.isActive);
      if (activeLocations.length > 0) {
        initializeDefault(activeLocations[0].id);
      }
    }
  }, [isSuperAdmin, locations, initializeDefault]);

  // For multi-location users: default to primaryLocationId if no selection stored
  useEffect(() => {
    if (hasMultipleLocations && !selectedLocationId && user?.primaryLocationId) {
      setSelectedLocationId(user.primaryLocationId);
    }
  }, [hasMultipleLocations, selectedLocationId, user?.primaryLocationId, setSelectedLocationId]);

  // Search functionality
  const { searchTerm, setSearchTerm, debouncedTerm, clearSearch, hasSearch } = useContactSearch();
  const { data: searchResults, isLoading: isSearching } = useContactList({
    search: debouncedTerm,
    limit: 5,
  });

  // Notifications
  const { data: notificationsData, isLoading: isLoadingNotifications } = useNotifications({ limit: 10 });
  const { data: unreadData } = useUnreadCount();
  const { markAsRead, markAllAsRead, isMarkingAllAsRead } = useNotificationMutations();

  const notifications = (notificationsData?.notifications || []) as ApiNotification[];
  const unreadCount = unreadData?.count || 0;

  // Show search results when there's a search term
  useEffect(() => {
    if (hasSearch) {
      setShowSearchResults(true);
    }
  }, [hasSearch]);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    }

    if (showNotifications || showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showSearchResults]);

  const handleSearchResultClick = (contactId: string) => {
    clearSearch();
    setShowSearchResults(false);
    locationNavigate(`/contacts/${contactId}`);
  };

  const handleNotificationClick = (notification: ApiNotification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      markAsRead({ id: notification.id });
    }

    // Navigate based on entity type
    if (notification.entityType === 'appointment' && notification.entityId) {
      setShowNotifications(false);
      locationNavigate(`/calendar`);
    } else if (notification.entityType === 'contact' && notification.entityId) {
      setShowNotifications(false);
      locationNavigate(`/contacts/${notification.entityId}`);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  return (
    <header className="h-16 border-b border-slate-700 bg-slate-800/50 backdrop-blur-sm sticky top-0 z-30">
      <div className="h-full px-4 flex items-center justify-between gap-4">
        {/* Left section */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button - visible on mobile and tablet */}
          <button
            onClick={onMenuClick}
            className="md:hidden p-2 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-colors"
            aria-label="Menü öffnen"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Page title / breadcrumb */}
          <h1 className="text-lg font-semibold text-slate-100">{pageTitle}</h1>
          {isSuperAdmin && locations ? (
            <LocationSelector
              locations={locations}
              selectedLocationId={selectedLocationId}
              onSelect={handleLocationSwitch}
              showBackToAdmin
            />
          ) : hasMultipleLocations ? (
            <LocationSelector
              locations={userAssignedLocations}
              selectedLocationId={effectiveLocationId}
              onSelect={handleLocationSwitch}
            />
          ) : locationName ? (
            <div className="hidden sm:flex items-center gap-1.5 text-sm text-slate-400">
              <MapPin className="w-3.5 h-3.5" />
              <span>{locationName}</span>
            </div>
          ) : null}
        </div>

        {/* Center section - Search (conditionally rendered) */}
        {showSearch && <div className="hidden md:flex flex-1 max-w-md" ref={searchRef}>
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="search"
              name="header-search-contacts"
              placeholder="Kontakte suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => hasSearch && setShowSearchResults(true)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-form-type="other"
              data-lpignore="true"
              className="w-full pl-10 pr-10 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Search Results Dropdown */}
            {showSearchResults && hasSearch && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-slate-700 rounded-lg shadow-lg border border-slate-600 overflow-hidden z-50">
                {isSearching ? (
                  <div className="px-4 py-3 text-center text-sm text-slate-400">
                    Suche...
                  </div>
                ) : searchResults && searchResults.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto">
                    {searchResults.map((contact) => (
                      <button
                        key={contact.id}
                        onClick={() => handleSearchResultClick(contact.id)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-600 transition-colors text-left"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-200 truncate">
                            {contact.firstName} {contact.lastName}
                          </p>
                          <p className="text-xs text-slate-400 truncate">
                            {contact.phone || contact.email || 'Keine Kontaktdaten'}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-center text-sm text-slate-400">
                    Keine Kontakte gefunden
                  </div>
                )}
              </div>
            )}
          </div>
        </div>}

        {/* Right section */}
        <div className="flex items-center gap-2">
          {/* Onboarding Playbook — available to all employees on every page */}
          <button
            onClick={() => setShowPlaybook(true)}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-colors"
            aria-label="Anleitung öffnen"
            title="Anleitung"
          >
            <BookOpen className="w-5 h-5" />
          </button>

          {showActions && (
            <>
            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-colors"
                aria-label="Benachrichtigungen"
              >
                <Bell className="w-5 h-5" />
                {/* Notification badge */}
                {unreadCount > 0 && (
                  <span className={`absolute top-1 right-1 min-w-4 h-4 px-1 bg-orange-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center${unreadCount > 10 ? ' animate-notification-glow' : ''}`}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-slate-700 rounded-lg shadow-lg border border-slate-600 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-slate-600 flex items-center justify-between">
                    <h3 className="font-medium text-slate-200">Benachrichtigungen</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        disabled={isMarkingAllAsRead}
                        className="text-xs text-orange-400 hover:text-orange-300 disabled:opacity-50"
                      >
                        <Check className="w-3 h-3 inline mr-1" />
                        Alle gelesen
                      </button>
                    )}
                  </div>

                  {isLoadingNotifications ? (
                    <div className="py-8 text-center">
                      <div className="w-6 h-6 border-2 border-slate-500 border-t-orange-500 rounded-full animate-spin mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Laden...</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.map((notification) => {
                        const style = getNotificationStyle(notification.type);
                        return (
                          <button
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-600 transition-colors text-left border-b border-slate-600/50 last:border-b-0 ${
                              !notification.isRead ? 'bg-slate-600/30' : ''
                            }`}
                          >
                            <div className={`mt-0.5 ${style.color}`}>
                              {style.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium truncate ${notification.isRead ? 'text-slate-300' : 'text-slate-100'}`}>
                                  {notification.title}
                                </p>
                                {!notification.isRead && (
                                  <span className="w-2 h-2 bg-orange-500 rounded-full shrink-0" />
                                )}
                              </div>
                              {notification.message && (
                                <p className="text-xs text-slate-400 truncate mt-0.5">
                                  {notification.message}
                                </p>
                              )}
                              <p className="text-xs text-slate-500 mt-1">
                                {formatTime(notification.createdAt)}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <BellOff className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Keine Benachrichtigungen</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quick actions */}
            <Button
              size="sm"
              className="hidden sm:flex"
              onClick={onWalkInClick}
              disabled={isSuperAdmin && !selectedLocationId}
              title={isSuperAdmin && !selectedLocationId ? 'Bitte zuerst einen Standort auswählen' : undefined}
            >
              + Walk-In
            </Button>
            </>
          )}
        </div>
      </div>

      {/* Onboarding Playbook overlay */}
      <PlaybookModal isOpen={showPlaybook} onClose={() => setShowPlaybook(false)} />
    </header>
  );
}
